import { Component, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CommonModule } from '@angular/common';
import { ToggleButtonChangeEvent, ToggleButtonModule } from 'primeng/togglebutton';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { PeerReviewerState } from '../../models/enums/peerReviewerState';
import { PeerReviewerResponse } from '../../models/super-admin/peerReviewerResponse';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthRoles, Course, Gender, GenderDescriptions } from '../../models/enums/enums';
import { MessageService } from 'primeng/api';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { of, switchMap } from 'rxjs';
import { UserStateModel } from '../../states/user/user-state-model';
import { PeerReviewersTimingsAndScheduledCallsResponse } from '../../models/super-admin/peerReviewersTimingsAndScheduledCallsResponse';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { UtilityService } from '../../services/utility/utility.service';
import { countryResponse } from '../../models/country/countryResponse';
import { InstituteService } from '../../services/institute/institute.service';
import { SearchInstituteResponse } from '../../models/institute/searchInstituteResponse';
import { GetPeerReviewerRequest } from '../../models/super-admin/getPeerReviewerRequest';


interface DropdownOption {
  name: string;
  value: PeerReviewerState;
}
interface degreePreference {
  name: string,
  key: Course
}

@Component({
  selector: 'app-peer-list',
  standalone: true,
  imports: [CalendarModule, CommonModule, FormsModule, ReactiveFormsModule, AutoCompleteModule, ToggleButtonModule, InputTextModule, TableModule, ButtonModule, DropdownModule],
  templateUrl: './peer-list.component.html',
  styleUrl: './peer-list.component.scss'
})
export class PeerListComponent implements OnInit {

  user: UserStateModel | undefined;
  totalRecords: number = 0;
  first: number = 0;
  rowSize: number = 10;
  pageSize = 10;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  peerState: PeerReviewerState | null = null;
  peerStateOptions: DropdownOption[] = [];
  peerList: PeerReviewerResponse[] = [];
  filterForm!: FormGroup;
  genders: { name: string; value: number }[] = [];
  adminNames: SearchByNameResponse[] = [];
  peerNames: SearchByNameResponse[] = [];
  countries: countryResponse[] = [];
  adminFilterId: string | undefined;
  searchAutoCompleteInstitutes: any;



  readonly PeerReviewerState = PeerReviewerState;
  degreePrefs = [
    { name: 'Bachelors', key: 'Bachelors' },
    { name: 'Masters', key: 'Masters' }
    // { name: 'Certification Course', key: 'Certification Course' },
    // { name: 'Phd', key: 'Phd' },
  ];

  isLoading: boolean = false;
  updatingState: { [key: string]: boolean } = {};
  submitted: boolean = false;

  constructor(
    private peerReviewService: PeerReviewService,
    private messageService: MessageService,
    private utilityService: UtilityService,
    private instituteService: InstituteService,
    private fb: FormBuilder,
    private router: Router,
    private store: Store
  ) { }
  initializeForm() {
    this.filterForm = this.fb.group({
      peerName: [null],
      adminNameId: [null],
      collegeName: [null],
      country: [null],
      fieldOfStudy: [null],
      levelOfStudy: [null],
      fromDate: [null],
      toDate: [null],
      status: [null]
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.populateDropdownFromEnum();
    this.getPeersCombined(1);
    this.loadCountries();
    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ]
  }

  editPeerDetails(id: string) {
    this.router.navigate(['/admin', 'peer-signup'], { queryParams: { edit: 'true', id } });
  }

  onAdminSelect(selectedAdmin: any): void {
    this.adminFilterId = selectedAdmin.value.id.toString();
  }

  private loadCountries(): void {
    this.utilityService.getCountries().subscribe({
      next: (response) => {
        if (response?.result) {
          this.countries = response.result;
        }
      },
      error: (err) => console.warn('Error loading countries:', err),
    });
  }

  filterInstitutes(event: AutoCompleteCompleteEvent): void {
    if (event.query.length > 3) {
      this.instituteService.searchInstitute(event.query).subscribe({
        next: (response: SearchInstituteResponse[]) => {
          this.searchAutoCompleteInstitutes = response.length > 0 ? response : [{ name: event.query }];
        }
      });
    } else {
      this.searchAutoCompleteInstitutes = [{ name: event.query }];
    }
  }
  filterAdminNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Admin',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.adminNames = response.result
        }
      });
    } else {
      this.adminNames = [];
    }
  }
  filterPeerNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'PeerReviewer',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.peerNames = response.result
        }
      });
    } else {
      this.peerNames = [];
    }
  }

  viewProfile(peerid: string): void {
    this.router.navigate(['/admin', 'timings', peerid]);
  }

  getPeersCombined(pageNumber: Number) {
    this.store.select(UserSelectors.getUser).pipe(
      switchMap(user => {
        if (!user || !user.id) {
          return of(null);
        }
        this.user = user;
        return of(user); // Ensure an observable is returned
      })
    ).subscribe({
      next: (response) => {
        if (response != null) {
          if (response.role.includes(AuthRoles.SuperAdmin)) {
            this.getPeers(pageNumber);
          }
          else if (response.role.includes(AuthRoles.Admin)) {
            this.getPeersByAdmin(pageNumber);
          }
        }
      },
      error: (error) => {
        console.warn('Error fetching user data', error);
      }
    })
  }

  onAdminStatusChange(event: ToggleButtonChangeEvent, peer: PeerReviewerResponse | PeerReviewersTimingsAndScheduledCallsResponse) {
    const newStatus = event.checked ? PeerReviewerState.Active : PeerReviewerState.Inactive;
    const peerId = 'applicationUserId' in peer ? peer.applicationUserId.toString() : peer.peerReviewerId.toString();
    this.editPeer(peerId, newStatus)
  }

  acceptPeer(peer: PeerReviewerResponse) {
    const peerId = peer.applicationUserId.toString();
    this.editPeer(peerId, PeerReviewerState.Active);
  }

  rejectPeer(peer: PeerReviewerResponse) {
    const peerId = peer.applicationUserId.toString();
    this.editPeer(peerId, PeerReviewerState.Inactive);
  }

  private editPeer(peerId: string, newStatus: PeerReviewerState) {
    this.updatingState[peerId] = true;
    this.peerReviewService.updatePeerReviewer({
      peerReviewerId: peerId,
      peerReviewerState: newStatus
    }).subscribe({
      next: (response) => {
        this.updatingState[peerId] = false;
        if (this.user?.role.includes(AuthRoles.SuperAdmin)) {
          this.getPeers(1);
        } else if ((this.user?.role.includes(AuthRoles.Admin))) {
          this.getPeersByAdmin(1);
        }
        const detail = newStatus === PeerReviewerState.Active
          ? 'Peer activated successfully. Activation email has been triggered.'
          : 'Peer updated successfully';
        this.messageService.add({ severity: 'success', summary: 'Success', detail });
      },
      error: (err) => {
        this.updatingState[peerId] = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating Peer' });
      }
    })
  }

  private getPeersByAdmin(pageNumber: Number) {
    this.isLoading = false;
    const getPeerReviewerRequest: GetPeerReviewerRequest = {
      peerReviewerName: this.filterForm.value.peerName,
      adminId: this.user?.id,
      instituteName: this.filterForm.value.collegeName,
      country: this.filterForm.value.country,
      fieldOfStudy: this.filterForm.value.fieldOfStudy,
      levelOfStudy: this.filterForm.value.levelOfStudy,
      startDate: this.filterForm.value.fromDate,
      endDate: this.filterForm.value.toDate,
      peerReviewerState: this.filterForm.value.status,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    }
    this.peerReviewService.getAllPeerReviewers(getPeerReviewerRequest).subscribe({
      next: (response) => {
        if (response) {
          this.peerList = response.result;
          this.totalRecords = response.total;
          this.isLoading = false;
        } else {
          this.peerList = [];
          this.totalRecords = 0;
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching Peers' });
      }
    })
  }
  private getPeers(pageNumber: Number) {
    this.isLoading = true;
    const getPeerReviewerRequest: GetPeerReviewerRequest = {
      peerReviewerName: this.filterForm.value.peerName,
      adminId: this.adminFilterId,
      instituteName: this.filterForm.value.collegeName,
      country: this.filterForm.value.country,
      fieldOfStudy: this.filterForm.value.fieldOfStudy,
      levelOfStudy: this.filterForm.value.levelOfStudy,
      startDate: this.filterForm.value.fromDate,
      endDate: this.filterForm.value.toDate,
      peerReviewerState: this.filterForm.value.status,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    }
    this.peerReviewService.getAllPeerReviewers(getPeerReviewerRequest).subscribe({
      next: (response) => {
        if (response) {
          this.peerList = response.result;
          this.totalRecords = response.total;
          this.isLoading = false;
        } else {
          this.peerList = [];
          this.totalRecords = 0;
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching Peers' });
      }
    })
  }

  genderDescriptions(arg0: Gender | undefined): string {
    if (arg0) {
      return GenderDescriptions[arg0];
    }
    else {
      return '';
    }
  }

  private populateDropdownFromEnum() {
    this.peerStateOptions = Object.keys(PeerReviewerState).filter(key => isNaN(Number(key))).map(key => ({
      name: key,
      value: PeerReviewerState[key as keyof typeof PeerReviewerState]
    }));
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.getPeersCombined(pageNumber);
  }

  addPeer() {
    this.router.navigate(['/admin', 'peer-signup']);
  }

  shouldShowAcceptButton(peer: PeerReviewerResponse): boolean {
    // Show Accept button for new users (Unknown) or rejected users (Inactive)
    return peer.state === PeerReviewerState.Unknown || peer.state === PeerReviewerState.Inactive;
  }

  shouldShowRejectButton(peer: PeerReviewerResponse): boolean {
    // Show Reject button for new users (Unknown) or accepted users (Active)
    return peer.state === PeerReviewerState.Unknown || peer.state === PeerReviewerState.Active;
  }

}
